/**
 * Report a handled Supabase (non-edge) query error to Sentry.
 *
 * Companion to captureEdgeFailure (reportEdgeFailure.ts). A `supabase.from(...)`
 * query returns `{ data, error }` — it doesn't throw — so RLS denials and query
 * errors that call sites handle (log + return null) never reach Sentry. Route
 * those error branches through this so they surface, grouped by table+operation
 * +Postgres code (so one recurring RLS denial is one issue, not a flood).
 */
import * as Sentry from "@sentry/react";

export type QueryOperation =
  | "select"
  | "insert"
  | "update"
  | "upsert"
  | "delete"
  | "rpc";

export interface QueryFailure {
  /** Table (or rpc name), e.g. "profiles". */
  table: string;
  operation: QueryOperation;
  /** The PostgrestError (`{ code, message, ... }`) or thrown error. */
  error: unknown;
}

/** Pull the Postgres error code off a PostgrestError, when present. */
function pgCode(error: unknown): string | undefined {
  const code = (error as { code?: unknown } | null | undefined)?.code;
  return typeof code === "string" ? code : undefined;
}

export function captureQueryError({ table, operation, error }: QueryFailure): void {
  const code = pgCode(error);
  const message =
    error instanceof Error
      ? error.message
      : (error as { message?: string } | null | undefined)?.message;

  Sentry.captureMessage(
    `Supabase ${operation} ${table} failed${code ? ` [${code}]` : ""}`,
    {
      level: "warning",
      tags: {
        supabase_table: table,
        supabase_op: operation,
        ...(code ? { supabase_code: code } : {}),
      },
      fingerprint: ["supabase-query", table, operation, code ?? "unknown"],
      extra: { message },
    },
  );
}
